import { CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import CancelFiltersButton from '../../../components/common/CancelFiltersButton'
import CustomContainer from '../../../components/common/CustomContainer'
import DateRangePicker from '../../../components/common/DateRangePicker'
import TabPanel from '../../../components/common/TabPanel'
import TextSearchField from '../../../components/common/TextSearchField'
import EmailHelp from '../../../components/emails/EmailHelp'
import ScreenLoadingPlaceholder from '../../../components/ScreenLoadingPlaceholder'
import { useAppDispatch } from '../../../hooks/reduxHooks'
import { RouteConstants } from '../../../navigation/navigation-types'
import { EmailManager } from '../../../services/api/EmailManager'
import {
    EmailMessageDto,
    EmailMessageState,
    EmailTemplateDto,
    SurveyResponseBriefDto,
} from '../../../services/api/sharedTypes/Email'
import { EmailAutomationSettingsDto } from '../../../services/api/sharedTypes/EmailAutomationSettings'
import { displayMessage } from '../../../services/redux/snackbar/snackbar-actions'
import { convertNumberToEmailTab, convertSurveyTypeToString, getEmailTab, getEmailTabNumber } from '../../../utils/email-utils'
import { convertToSearchString, getTimeSpanFromFormattedTimeSpan, openInNewTab } from '../../../utils/global-utils'
import EmailSettings from '../settings/EmailSettings'
import EmailTemplateTable from '../tables/EmailTemplateTable'
import FilledSurveysTable from '../tables/FilledSurveysTable'
import WaitingEmailsTable from '../tables/WaitingEmailsTable'

const EmailsAndSurveys: React.FC = () => {
    const [selectedTabValue, setSelectedTabValue] = useState<number>(
        getEmailTabNumber(getEmailTab(location.hash.replace('#', ''))) ?? 0
    )
    const [dateFrom, setDateFrom] = useState<Date | null>(null)
    const [dateTo, setDateTo] = useState<Date | null>(null)
    const [searchedValue, setSearchedValue] = useState<string>('')
    const [emails, setEmails] = useState<EmailMessageDto[]>([])
    const [filteredEmails, setFilteredEmails] = useState<EmailMessageDto[]>([])
    const [emailTemplates, setEmailTemplates] = useState<EmailTemplateDto[]>([])
    const [emailAutomationSettings, setEmailAutomationSettings] = useState<EmailAutomationSettingsDto>()
    const [surveys, setSurveys] = useState<SurveyResponseBriefDto[]>([])
    const [loadingData, setLoadingData] = useState<boolean>(false)
    const [loadingSurveyData, setLoadingSurveyData] = useState<boolean>(false)
    const [loadingDateFilter, setLoadingDateFilter] = useState<boolean>(false)
    const [loadingSettings, setLoadingSettings] = useState<boolean>(false)

    const dispatch = useAppDispatch()

    const handleChangeTabValue = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, newValue: number) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (event.metaKey || event.button === 1 || event.ctrlKey) {
            openInNewTab(RouteConstants.emailsAndSurveys + '#' + convertNumberToEmailTab(newValue))
        } else {
            setSelectedTabValue(newValue)
            location.hash = convertNumberToEmailTab(newValue)
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true)
            try {
                const emailMessagesRes = await EmailManager.getManager().getEmailMessagesListAsync()
                const emailTemplatesRes = await EmailManager.getManager().getEmailTemplatesListAsync()
                if (emailMessagesRes) {
                    setEmails(emailMessagesRes.messages)
                    setFilteredEmails(emailMessagesRes.messages)
                }
                if (emailTemplatesRes) {
                    setEmailTemplates(emailTemplatesRes.emailTemplates)
                }
            } catch (error) {
                setLoadingData(false)
                return dispatch(displayMessage({ message: error, type: 'error' }))
            }

            setLoadingData(false)
        }
        const fetchSurveys = async () => {
            setLoadingSurveyData(true)
            try {
                const surveysRes = await EmailManager.getManager().getSurveysListAsync()
                if (surveysRes) {
                    setSurveys(surveysRes.surveyResponses)
                }
            } catch (error) {
                setLoadingSurveyData(false)
                return dispatch(displayMessage({ message: error, type: 'error' }))
            }
            setLoadingSurveyData(false)
        }
        const fetchSettings = async () => {
            setLoadingSettings(true)
            try {
                const settingsRes = await EmailManager.getManager().getEmailAutomationSettingsAsync()
                if (settingsRes) {
                    setEmailAutomationSettings(settingsRes)
                }
            } catch (error) {
                setLoadingSettings(false)
                return dispatch(displayMessage({ message: error, type: 'error' }))
            }
            setLoadingSettings(false)
        }
        fetchData()
        fetchSurveys()
        fetchSettings()
    }, [setLoadingData])

    const setFilteredEventsByEventDate = async () => {
        if (!emails || emails.length <= 0) return
        setLoadingDateFilter(true)
        if (dateTo && dateFrom) {
            try {
                if (isNaN(dateTo.getTime()) || isNaN(dateFrom.getTime())) return setLoadingDateFilter(false)
                const emailsRes = await EmailManager.getManager().getFilteredEmailMessagesListAsync(
                    dateFrom.toISOString(),
                    dateTo.toISOString()
                )
                if (emailsRes) {
                    setFilteredEmails(emailsRes.messages)
                }
                const surveysRes = await EmailManager.getManager().getFilteredSurveysListAsync(
                    dateFrom.toISOString(),
                    dateTo.toISOString()
                )
                if (surveysRes) {
                    setSurveys(surveysRes.surveyResponses)
                }
            } catch (error) {
                setLoadingDateFilter(false)
                return dispatch(displayMessage({ message: error, type: 'error' }))
            }
        } else {
            const emailsRes = await EmailManager.getManager().getEmailMessagesListAsync()
            const surveysRes = await EmailManager.getManager().getSurveysListAsync()
            if (emailsRes) {
                setEmails(emailsRes.messages)
                setFilteredEmails(emailsRes.messages)
            }
            if (surveysRes) {
                setSurveys(surveysRes.surveyResponses)
            }
        }
        setLoadingDateFilter(false)
    }
    useEffect(() => {
        setFilteredEventsByEventDate()
    }, [dateFrom, dateTo, setLoadingDateFilter])

    const updateTemplateMessage = async (id: string, subject: string, emailBody: string, isEnabled: boolean) => {
        try {
            const res = await EmailManager.getManager().updateEmailTemplateAsync(id, subject, emailBody, isEnabled)
            if (res) {
                setEmailTemplates((prev) => {
                    const index = prev.findIndex((item) => item.id === id)
                    if (index !== -1) {
                        const newEmails = [...prev]
                        newEmails[index] = res
                        return newEmails
                    }
                    return prev
                })
                return dispatch(displayMessage({ message: 'Email byl úspěšně upraven', type: 'success' }))
            }
        } catch (error) {
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
    }

    const updateEmailMessage = async (id: string, subject: string, emailBody: string, updateAndSendEmailMessage?: boolean) => {
        try {
            const res = await EmailManager.getManager().updateEmailMessageAsync(id, subject, emailBody)
            if (res) {
                setEmails(emails.map((email) => (email.id === id ? res : email)))
                setFilteredEmails(emails.map((email) => (email.id === id ? res : email)))
                if (updateAndSendEmailMessage) {
                    return sendEmailMessage(id)
                }
                return dispatch(displayMessage({ message: 'Email byl úspěšně upraven', type: 'success' }))
            }
        } catch (error) {
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
    }

    const updateEmailAutomationSettings = async (settings: EmailAutomationSettingsDto) => {
        try {
            const fixedSettings: EmailAutomationSettingsDto = {
                remindPendingOfferTimeout: getTimeSpanFromFormattedTimeSpan(settings.remindPendingOfferTimeout),
                remindApproachingEventStartTimeout: getTimeSpanFromFormattedTimeSpan(settings.remindApproachingEventStartTimeout),
                remindNotPaidEventTimeout_Severity1: getTimeSpanFromFormattedTimeSpan(
                    settings.remindNotPaidEventTimeout_Severity1
                ),
                remindNotPaidEventTimeout_Severity2: getTimeSpanFromFormattedTimeSpan(
                    settings.remindNotPaidEventTimeout_Severity2
                ),
                remindEventRepetitionTimeoutMonths: settings.remindEventRepetitionTimeoutMonths,
            }
            const res = await EmailManager.getManager().updateEmailAutomationSettingsAsync(fixedSettings)
            if (res) {
                setEmailAutomationSettings(res)
                return dispatch(displayMessage({ message: 'Nastavení bylo úspěšně upraveno', type: 'success' }))
            }
        } catch (error) {
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
    }

    const deleteEmailMessage = async (id: string) => {
        try {
            await EmailManager.getManager().deleteEmailMessageAsync(id)
            setEmails(emails.filter((email) => email.id !== id))
            setFilteredEmails(emails.filter((email) => email.id !== id))
            dispatch(displayMessage({ message: 'Email byl úspěšně smazán', type: 'success' }))
        } catch (error) {
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
    }

    const sendEmailMessage = async (id: string) => {
        try {
            await EmailManager.getManager().sendEmailMessageAsync(id)
            setEmails(emails.map((email) => (email.id === id ? { ...email, state: EmailMessageState.Sent } : email)))
            setFilteredEmails(emails.map((email) => (email.id === id ? { ...email, state: EmailMessageState.Sent } : email)))
            dispatch(displayMessage({ message: 'Email byl úspěšně odeslán', type: 'success' }))
        } catch (error) {
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
    }

    const emailsForTable = filteredEmails?.filter((email) => {
        if (searchedValue) {
            return (
                convertToSearchString(email.emailRecipient ?? '').includes(convertToSearchString(searchedValue)) ||
                convertToSearchString(email.subject ?? '').includes(convertToSearchString(searchedValue)) ||
                convertToSearchString(email.body ?? '').includes(convertToSearchString(searchedValue)) ||
                convertToSearchString(email.eventIdName?.name ?? '').includes(convertToSearchString(searchedValue))
            )
        }
        return true
    })

    const surveysForTable = surveys?.filter((survey) => {
        if (searchedValue) {
            return (
                convertToSearchString(survey.address.city).includes(convertToSearchString(searchedValue)) ||
                convertToSearchString(survey.address.street).includes(convertToSearchString(searchedValue)) ||
                convertToSearchString(survey.address.zip).includes(convertToSearchString(searchedValue)) ||
                convertToSearchString(survey.respondentIdName.name).includes(convertToSearchString(searchedValue)) ||
                convertToSearchString(survey.eventIdName.name).includes(convertToSearchString(searchedValue)) ||
                convertToSearchString(convertSurveyTypeToString(survey.surveyType)).includes(convertToSearchString(searchedValue))
            )
        }
        return true
    })

    if (loadingData) return <ScreenLoadingPlaceholder />

    return (
        <CustomContainer>
            <Typography variant='h5'>Emaily a dotazníky</Typography>

            <Tabs value={selectedTabValue}>
                <Tab label='Čeká na vyřízení' onMouseDown={(e) => handleChangeTabValue(e, 0)} />
                <Tab label='Šablony' onMouseDown={(e) => handleChangeTabValue(e, 1)} />
                <Tab label='Vyplněné dotazníky' onMouseDown={(e) => handleChangeTabValue(e, 2)} />
                <Tab label='Nastavení' onMouseDown={(e) => handleChangeTabValue(e, 3)} />
            </Tabs>

            <TabPanel value={selectedTabValue} index={0}>
                <Stack direction='row' gap={3} mt={1} mb={3} overflow='hidden'>
                    <DateRangePicker
                        labelFrom='Vytvořeny od'
                        labelTo='Vytvořeny do'
                        dateTo={dateTo}
                        dateFrom={dateFrom}
                        onSetDateFrom={setDateFrom}
                        onSetDateTo={setDateTo}
                    />
                    <TextSearchField value={searchedValue} onChange={(e) => setSearchedValue(e.target.value)} />
                    {
                        searchedValue || dateFrom || dateTo ? (
                            <CancelFiltersButton onClick={
                                () => {
                                    setSearchedValue('')
                                    setDateFrom(null)
                                    setDateTo(null)
                                }
                            }
                            />
                        ) : null
                    }
                </Stack>

                {loadingDateFilter ? (
                    <CircularProgress />
                ) : (
                    <WaitingEmailsTable
                        emails={emailsForTable.filter((e) => e.state === EmailMessageState.WaitingToBeSent)}
                        onUpdate={updateEmailMessage}
                        onDelete={deleteEmailMessage}
                    />
                )}
            </TabPanel>

            <TabPanel value={selectedTabValue} index={1}>
                <EmailTemplateTable emails={emailTemplates} onSave={updateTemplateMessage} />
                <EmailHelp />
            </TabPanel>

            <TabPanel value={selectedTabValue} index={2}>
                <Stack direction='row' gap={3} mt={1} mb={3} overflow='hidden'>
                    <DateRangePicker
                        labelFrom='Vytvořeny od'
                        labelTo='Vytvořeny do'
                        dateTo={dateTo}
                        dateFrom={dateFrom}
                        onSetDateFrom={setDateFrom}
                        onSetDateTo={setDateTo}
                    />
                    <TextSearchField value={searchedValue} onChange={(e) => setSearchedValue(e.target.value)} />
                    {
                        searchedValue || dateFrom || dateTo ? (
                            <CancelFiltersButton onClick={
                                () => {
                                    setSearchedValue('')
                                    setDateFrom(null)
                                    setDateTo(null)
                                }
                            }
                            />
                        ) : null
                    }
                </Stack>
                {loadingSurveyData || loadingDateFilter ? <CircularProgress /> : <FilledSurveysTable surveys={surveysForTable} />}
            </TabPanel>

            <TabPanel value={selectedTabValue} index={3}>
                {loadingSettings ? (
                    <CircularProgress />
                ) : !emailAutomationSettings ? (
                    <Typography variant='h6'>Nastavení není k dispozici</Typography>
                ) : (
                    <EmailSettings
                        emailAutomationSettings={emailAutomationSettings}
                        onChange={setEmailAutomationSettings}
                        onSave={updateEmailAutomationSettings}
                    />
                )}
            </TabPanel>
        </CustomContainer>
    )
}

export default EmailsAndSurveys
